import { VIEWS } from '@constants/tasks';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ValueOf } from '@utils/tsUtils';
import { endOfWeek, startOfWeek } from 'date-fns';
import {
  IActiveTask,
  ITask,
  IListMeta,
  ITaskTemplate,
  TaskFiltersType,
  IActiveTaskInfo,
} from 'types/types';

export interface TasksState {
  tasks: ITask[];
  filters: TaskFiltersType;
  prevFilters: TaskFiltersType;
  templates: ITaskTemplate[];
  activeTask: IActiveTask;
  prevActiveTaskId: string | null;
  isLoadingTasksList: boolean;
  isLoadingActiveTask: boolean;
  isUpdatingActiveTask: boolean;
  isShownCreateNewTaskPopup: boolean;
  isCreatingNewTask: boolean;
  isOpenedActiveTask: boolean;
  tasksListMeta: IListMeta;
  isNextPageLoading: boolean;
  isFirstTimeLoading: boolean;
  view: ValueOf<typeof VIEWS>;
  selectedDate: Date;
  entitiesSearchResults: any[];
  isLoadingEntities: boolean;
}

const initialTasksListMeta = {
  page: 1,
  size: 50,
  total: 0,
  totalPages: 1,
};

const initialTaskListMeta = {
  page: 1,
  size: 25,
  total: 0,
  totalPages: 1,
};

const initialActiveTask = {
  taskInfo: null,
  logsList: [],
  commentsList: [],
  isTaskLogsLoading: false,
  isTaskCommentsLoading: false,
  logsListMeta: initialTaskListMeta,
  commentsListMeta: initialTaskListMeta,
  isSendingTaskComment: false,
  commentBody: '',
  isNextCommentsPageLoading: false,
};

const initialState: TasksState = {
  tasks: [],
  filters: {},
  prevFilters: {},
  templates: [],
  isLoadingTasksList: false,
  activeTask: initialActiveTask,
  prevActiveTaskId: null,
  isShownCreateNewTaskPopup: false,
  isCreatingNewTask: false,
  isLoadingActiveTask: false,
  isUpdatingActiveTask: false,
  isOpenedActiveTask: false,
  tasksListMeta: initialTasksListMeta,
  isNextPageLoading: false,
  isFirstTimeLoading: false,
  view: VIEWS.LIST,
  selectedDate: new Date(),
  entitiesSearchResults: [],
  isLoadingEntities: false,
};

export const name = 'tasks';

const tasksSlice = createSlice({
  name,
  initialState,
  reducers: {
    setTasks(state, { payload }: PayloadAction<ITask[]>) {
      state.tasks = payload;
      state.isFirstTimeLoading = false;
    },
    setFilters(state, { payload }: PayloadAction<TaskFiltersType>) {
      state.prevFilters = { ...state.filters };
      state.filters = payload;
      state.tasks = [];
      state.tasksListMeta = initialTasksListMeta;
      state.isFirstTimeLoading = true;
    },
    addTask(state, { payload }: PayloadAction<ITask>) {
      state.tasks = [payload, ...state.tasks];
    },
    updateTaskInList(state, { payload }: PayloadAction<ITask>) {
      const task = state.tasks.find(task => task.id === payload.id);
      if (task) {
        Object.assign(task, payload);
      }
    },

    deleteTaskFromList(state, { payload }: PayloadAction<string>) {
      const index = state.tasks.findIndex(task => task.id === payload);
      if (index !== -1) {
        state.tasks.splice(index, 1);
      }
    },

    setActiveTask(state, { payload }: PayloadAction<IActiveTaskInfo>) {
      state.activeTask.taskInfo = payload;
    },
    updateActiveTaskInfo(state, { payload }: PayloadAction<IActiveTaskInfo>) {
      state.activeTask.taskInfo = { ...state.activeTask.taskInfo, ...payload };
    },
    removeActiveTask(state) {
      if (!state.activeTask.taskInfo) return;

      state.prevActiveTaskId = state.activeTask.taskInfo.id;
      state.activeTask = initialActiveTask;
    },
    setTaskLogs(state, { payload }) {
      if (!state.activeTask) return;

      state.activeTask.logsList = payload;
    },
    setTaskLogsMeta(state, { payload }) {
      if (!state.activeTask) return;

      state.activeTask.logsListMeta = payload;
    },
    setTaskLogsPage(state, { payload }: PayloadAction<IListMeta['page']>) {
      if (!state.activeTask || !state.activeTask.logsListMeta) return;

      state.activeTask.logsListMeta.page = payload;
    },

    setTaskComments(state, { payload }) {
      if (!state.activeTask) return;

      state.activeTask.commentsList = payload;
    },
    setTaskCommentsPage(state, { payload }: PayloadAction<IListMeta['page']>) {
      if (!state.activeTask || !state.activeTask.commentsListMeta) return;

      state.activeTask.commentsListMeta.page = payload;
    },
    setTaskCommentsMeta(state, { payload }) {
      if (!state.activeTask) return;

      state.activeTask.commentsListMeta = payload;
    },
    setTaskCommentBody(state, { payload }: PayloadAction<string>) {
      if (!state.activeTask) return;

      state.activeTask.commentBody = payload;
    },
    setIsSendingTaskComment(state, { payload }: PayloadAction<boolean>) {
      if (!state.activeTask) return;

      state.activeTask.isSendingTaskComment = payload;
    },
    setIsNextCommentsPageLoading(state, { payload }: PayloadAction<boolean>) {
      if (!state.activeTask) return;

      state.activeTask.isNextCommentsPageLoading = payload;
    },

    addTaskLogInList(state, { payload }) {
      if (!state.activeTask || !state.activeTask.logsList) return;

      state.activeTask.logsList.unshift(payload);
    },

    addTaskCommentInList(state, { payload }) {
      if (!state.activeTask || !state.activeTask.commentsList) return;

      state.activeTask.commentsList.push(payload);
    },

    setIsLoadingTasksList(state, { payload }: PayloadAction<boolean>) {
      state.isLoadingTasksList = payload;
    },
    setIsLoadingActiveTask(state, { payload }: PayloadAction<boolean>) {
      state.isLoadingActiveTask = payload;
    },
    setIsUpdatingActiveTask(state, { payload }: PayloadAction<boolean>) {
      state.isUpdatingActiveTask = payload;
    },
    setIsOpenedActiveTask(state, { payload }: PayloadAction<boolean>) {
      state.isOpenedActiveTask = payload;
    },
    setIsTaskLogsLoading(state, { payload }: PayloadAction<boolean>) {
      state.activeTask.isTaskLogsLoading = payload;
    },
    setIsTaskCommentsLoading(state, { payload }: PayloadAction<boolean>) {
      state.activeTask.isTaskCommentsLoading = payload;
    },
    setIsShownCreateNewTaskPopup(state, { payload }: PayloadAction<boolean>) {
      state.isShownCreateNewTaskPopup = payload;
    },
    setIsCreatingNewTask(state, { payload }: PayloadAction<boolean>) {
      state.isCreatingNewTask = payload;
    },
    setTasksListMeta(state, { payload }: PayloadAction<IListMeta>) {
      state.tasksListMeta = payload;
    },
    setIsNextPageLoading(state, { payload }: PayloadAction<boolean>) {
      state.isNextPageLoading = payload;
    },
    setIsFirstTimeLoading(state, { payload }: PayloadAction<boolean>) {
      state.isFirstTimeLoading = payload;
    },
    setTasksPage(state, { payload }: PayloadAction<IListMeta['page']>) {
      state.tasksListMeta.page = payload;
    },
    setTasksTemplates(state, { payload }: PayloadAction<ITaskTemplate[]>) {
      state.templates = payload;
    },

    setView(state, { payload }: PayloadAction<ValueOf<typeof VIEWS>>) {
      state.view = payload;
    },
    setSelectedDate(state, { payload }: PayloadAction<Date>) {
      state.selectedDate = payload;

      const weekStartISO = startOfWeek(payload, {
        weekStartsOn: 1,
      }).toISOString();
      const weekEndISO = endOfWeek(payload, {
        weekStartsOn: 1,
      }).toISOString();

      state.filters = {
        ...state.filters,
        startedAtFrom: weekStartISO,
        endedAtTo: weekEndISO,
      };
    },
    setEntitiesSearchResults(state, { payload }: PayloadAction<any[]>) {
      state.entitiesSearchResults = payload;
    },
    setIsLoadingEntities(state, { payload }: PayloadAction<boolean>) {
      state.isLoadingEntities = payload;
    },
  },
});

export const {
  setTasks,
  setFilters,
  addTask,
  updateTaskInList,
  deleteTaskFromList,
  setActiveTask,
  removeActiveTask,
  setTaskLogs,

  setTaskComments,
  addTaskCommentInList,
  setTaskCommentsMeta,
  setTaskCommentsPage,
  setTaskCommentBody,
  setIsSendingTaskComment,
  setIsNextCommentsPageLoading,
  setIsTaskCommentsLoading,

  setTaskLogsMeta,
  setIsLoadingActiveTask,
  setIsUpdatingActiveTask,
  setIsLoadingTasksList,
  setIsOpenedActiveTask,
  setIsTaskLogsLoading,
  setIsShownCreateNewTaskPopup,
  setIsCreatingNewTask,
  setTasksListMeta,
  setIsNextPageLoading,
  setIsFirstTimeLoading,
  setTasksPage,
  setTaskLogsPage,
  updateActiveTaskInfo,
  addTaskLogInList,
  setTasksTemplates,

  setView,
  setSelectedDate,
  setEntitiesSearchResults,
  setIsLoadingEntities,
} = tasksSlice.actions;

export default tasksSlice.reducer;
